import { useState, useRef, useEffect, useCallback } from 'react';
import { RecordingState, UseCameraRecorderOptions, RecordingHealth } from '../types';
import { video as videoApi, getApiBaseUrl } from '../../../services/api';
import { saveSessionRecordingBlob } from '../utils/mediaClientStorage';

export function useCameraRecorder(options: UseCameraRecorderOptions = {}) {
  const {
    attemptId,
    scenarioId,
    captureMode = 'AUDIO_VIDEO',
    autoStart = false,
    cameraStream,
    audioStream,
    stream: externalStream,
    onRecordingComplete,
    onError,
  } = options;

  const [recordingState, setRecordingState] = useState<RecordingState>('IDLE');
  const recordingStateRef = useRef<RecordingState>('IDLE');
  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<RecordingHealth>({
    isMicLive: true,
    isCameraLive: captureMode !== 'AUDIO_ONLY',
    isRecording: false,
    healthWarning: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const compositeStreamRef = useRef<MediaStream | null>(null);
  const internalCameraStreamRef = useRef<MediaStream | null>(null);
  const internalAudioStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<any>(null);
  const healthIntervalRef = useRef<any>(null);
  const uploadInitDetailsRef = useRef<any>(null);
  const startTimeRef = useRef<number>(0);
  const lastChunkTimestampRef = useRef<number>(0);
  const createdBlobUrlsRef = useRef<string[]>([]);
  const attemptIdRef = useRef<string | undefined>(attemptId);
  useEffect(() => {
    attemptIdRef.current = attemptId;
  }, [attemptId]);

  const cameraStreamRef = useRef<MediaStream | null>(cameraStream || null);
  cameraStreamRef.current = cameraStream || null;

  const audioStreamRef = useRef<MediaStream | null>(audioStream || null);
  audioStreamRef.current = audioStream || null;

  // Clean up timers, recorder, and all hardware media tracks on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch { }
      }
      if (internalCameraStreamRef.current) {
        internalCameraStreamRef.current.getTracks().forEach((track) => track.stop());
        internalCameraStreamRef.current = null;
      }
      if (internalAudioStreamRef.current) {
        internalAudioStreamRef.current.getTracks().forEach((track) => track.stop());
        internalAudioStreamRef.current = null;
      }
      if (compositeStreamRef.current) {
        compositeStreamRef.current.getTracks().forEach((track) => track.stop());
        compositeStreamRef.current = null;
      }
      createdBlobUrlsRef.current.forEach((url) => {
        // Retain the provisional blob URL if stored in sessionStorage for immediate results page replay
        if (typeof window !== 'undefined') {
          const currentAttId = attemptIdRef.current;
          if (currentAttId && sessionStorage.getItem(`trainova_attempt_video_${currentAttId}`) === url) {
            return;
          }
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('trainova_attempt_video_') && sessionStorage.getItem(key) === url) {
              return;
            }
          }
        }
        try { URL.revokeObjectURL(url); } catch { }
      });
      createdBlobUrlsRef.current = [];
      recordedChunksRef.current = [];
    };
  }, []);

  const isStartingRef = useRef<boolean>(false);

  const createMediaRecorderInstance = useCallback(
    (stream: MediaStream, mimeType?: string): MediaRecorder | null => {
      if (typeof MediaRecorder === 'undefined') return null;
      let effectiveMimeType = mimeType;
      const hasVideo = stream.getVideoTracks().length > 0;
      if (hasVideo && (!effectiveMimeType || effectiveMimeType.startsWith('audio/'))) {
        const videoCandidates = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp9,opus',
          'video/webm',
          'video/mp4;codecs=avc1',
          'video/mp4'
        ];
        effectiveMimeType = videoCandidates.find((m) => {
          try {
            return MediaRecorder.isTypeSupported(m);
          } catch {
            return false;
          }
        }) || 'video/webm';
      }

      let recorder: MediaRecorder;
      const recorderOptions: MediaRecorderOptions = {
        ...(effectiveMimeType ? { mimeType: effectiveMimeType } : {}),
        videoBitsPerSecond: hasVideo ? 2500000 : undefined,
        audioBitsPerSecond: 128000,
      };
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch (mErr) {
        console.warn('[SessionMeetingRecorder] Fallback to default MediaRecorder without bitrate options:', mErr);
        try {
          recorder = effectiveMimeType ? new MediaRecorder(stream, { mimeType: effectiveMimeType }) : new MediaRecorder(stream);
        } catch {
          recorder = new MediaRecorder(stream);
        }
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
          lastChunkTimestampRef.current = Date.now();
        }
      };

      recorder.onerror = (recErr: any) => {
        const errName = recErr?.error?.name || recErr?.name || 'Notice';
        console.warn(`[SessionMeetingRecorder] MediaRecorder notice (${errName})`);
        if (errName === 'InvalidStateError') return;
        setHealth((prev) => ({ ...prev, healthWarning: 'Recording stream encountered an interruption' }));
      };

      try {
        recorder.start(1000); // 1-second chunks for resilient streaming
        return recorder;
      } catch (startErr: any) {
        console.warn('[SessionMeetingRecorder] Could not start MediaRecorder instance:', startErr?.message);
        return null;
      }
    },
    []
  );

  const startRecording = useCallback(async () => {
    if (!attemptId || isStartingRef.current) {
      return;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      return;
    }

    isStartingRef.current = true;
    try {
      // Clean up any stale or orphaned MediaRecorder before initiating a new instance
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.ondataavailable = null;
          mediaRecorderRef.current.onerror = null;
          mediaRecorderRef.current.onstop = null;
          if (mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
        } catch {}
        mediaRecorderRef.current = null;
      }

      setRecordingState('STARTING');
      setError(null);

      const isAudioOnly = captureMode === 'AUDIO_ONLY';
      const audioCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/aac',
        'audio/ogg'
      ];
      const videoCandidates = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm',
        'video/mp4;codecs=avc1',
        'video/mp4'
      ];
      const candidates = isAudioOnly ? audioCandidates : videoCandidates;
      const supportedMimeType = typeof MediaRecorder !== 'undefined'
        ? candidates.find((m) => {
          try {
            return MediaRecorder.isTypeSupported(m);
          } catch {
            return false;
          }
        })
        : undefined;
      const defaultMimeType = supportedMimeType || (isAudioOnly ? 'audio/webm' : 'video/webm');

      // 1. Initialize upload session with backend (non-blocking for simulation)
      let initRes;
      try {
        initRes = await videoApi.initRecording(attemptId, {
          mimeType: defaultMimeType,
          scenarioId,
        });
      } catch (callErr: any) {
        console.warn('[SessionMeetingRecorder] Session upload init request failed:', callErr?.message);
      }

      if (!initRes || !initRes.success || !initRes.data) {
        const errorMsg = initRes?.error?.message || (initRes as any)?.message || 'Video recording upload endpoint unavailable';
        console.warn(`[SessionMeetingRecorder] Cloud upload session fallback (${errorMsg}). Proceeding with local in-session recording.`);
        const fallbackStorageKey = `tenants/default/attempts/${attemptId}/video/vrec_${Date.now()}.webm`;
        uploadInitDetailsRef.current = {
          recordingId: `vrec_${Date.now()}`,
          storageKey: fallbackStorageKey,
          storageProvider: 'local',
          uploadUrl: `/api/v1/mock-storage/upload?key=${encodeURIComponent(fallbackStorageKey)}`,
          uploadMethod: 'PUT',
          mimeType: defaultMimeType,
        };
      } else {
        uploadInitDetailsRef.current = initRes.data;
      }

      // 2. Prepare tracks: mixed audio + optional camera video
      const tracksToRecord: MediaStreamTrack[] = [];

      // A. Audio Track: Prefer mixedAudioStream from Web Audio, fallback to audioStream / externalStream / getUserMedia
      let activeAudioTrack: MediaStreamTrack | null = null;
      const effectiveAudioStream = audioStream || audioStreamRef.current;
      if (effectiveAudioStream && effectiveAudioStream.getAudioTracks().length > 0) {
        activeAudioTrack = effectiveAudioStream.getAudioTracks().find((t) => t.readyState === 'live') || effectiveAudioStream.getAudioTracks()[0];
      } else if (externalStream && externalStream.getAudioTracks().length > 0) {
        activeAudioTrack = externalStream.getAudioTracks().find((t) => t.readyState === 'live') || externalStream.getAudioTracks()[0];
      } else if (
        !('audioStream' in options) &&
        !('stream' in options) &&
        typeof navigator !== 'undefined' &&
        navigator.mediaDevices?.getUserMedia
      ) {
        try {
          const micMedia = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
          internalAudioStreamRef.current = micMedia;
          activeAudioTrack = micMedia.getAudioTracks()[0];
        } catch (e: any) {
          console.warn('[SessionMeetingRecorder] Standalone mic track notice:', e.message);
        }
      }

      if (activeAudioTrack) {
        tracksToRecord.push(activeAudioTrack);
      }

      // B. Video Track (if not AUDIO_ONLY): Prefer cameraStream, fallback to externalStream / getUserMedia
      if (!isAudioOnly) {
        let activeVideoTrack: MediaStreamTrack | null = null;
        const effectiveCameraStream = cameraStream || cameraStreamRef.current;
        if (effectiveCameraStream && effectiveCameraStream.getVideoTracks().length > 0) {
          activeVideoTrack = effectiveCameraStream.getVideoTracks()[0];
        } else if (externalStream && externalStream.getVideoTracks().length > 0) {
          activeVideoTrack = externalStream.getVideoTracks()[0];
        } else if ((!effectiveCameraStream || effectiveCameraStream.getVideoTracks().length === 0) && (!externalStream || externalStream.getVideoTracks().length === 0) && typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
          try {
            const camMedia = await navigator.mediaDevices.getUserMedia({
              video: { width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 }, frameRate: { ideal: 24, max: 30 } },
            });
            internalCameraStreamRef.current = camMedia;
            activeVideoTrack = camMedia.getVideoTracks()[0];
          } catch (e: any) {
            console.warn('[SessionMeetingRecorder] Camera capture notice:', e.message);
          }
        }

        if (activeVideoTrack) {
          tracksToRecord.push(activeVideoTrack);
        }
      }

      if (tracksToRecord.length === 0) {
        console.warn('[SessionMeetingRecorder] No valid media tracks (camera/mic) available for meeting recording. Recording disabled.');
        setRecordingState('DISABLED');
        return;
      }

      // 3. Create composite MediaStream
      const compositeStream = new MediaStream(tracksToRecord);
      compositeStreamRef.current = compositeStream;

      // 4. Initialize MediaRecorder using safe instance creator
      recordedChunksRef.current = [];
      lastChunkTimestampRef.current = Date.now();
      const recorder = createMediaRecorderInstance(compositeStream, supportedMimeType);
      if (recorder) {
        mediaRecorderRef.current = recorder;
      }

      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setRecordingState('RECORDING');
      setHealth({
        isMicLive: Boolean(activeAudioTrack && activeAudioTrack.readyState === 'live'),
        isCameraLive: Boolean(!isAudioOnly && tracksToRecord.some((t) => t.kind === 'video' && t.readyState === 'live')),
        isRecording: true,
        healthWarning: null,
      });

      // 5. Start elapsed timer
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      // 6. Continuous health monitoring
      healthIntervalRef.current = setInterval(() => {
        if (!compositeStreamRef.current) return;
        const audioTracks = compositeStreamRef.current.getAudioTracks();
        const videoTracks = compositeStreamRef.current.getVideoTracks();
        const micLive = audioTracks.length > 0 && audioTracks.every((t) => t.readyState === 'live');
        const camLive = isAudioOnly || (videoTracks.length > 0 && videoTracks.every((t) => t.readyState === 'live'));

        let warning: string | null = null;
        if (!micLive) warning = 'Microphone connection interrupted';
        else if (!camLive && !isAudioOnly && videoTracks.length > 0) warning = 'Camera connection interrupted';

        setHealth({
          isMicLive: micLive,
          isCameraLive: camLive,
          isRecording: mediaRecorderRef.current?.state === 'recording',
          healthWarning: warning,
        });
      }, 3000);
    } catch (err: any) {
      console.error('[SessionMeetingRecorder] startRecording error:', err);
      setRecordingState('FAILED');
      setError(err.message || 'Failed to start meeting session recording');
      if (onError) onError(err.message);
    } finally {
      isStartingRef.current = false;
    }
  }, [attemptId, scenarioId, captureMode, cameraStream, audioStream, externalStream, onError]);

  const stopRecording = useCallback(async () => {
    if (recordingState !== 'RECORDING' && recordingState !== 'STARTING') {
      return null;
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (healthIntervalRef.current) {
      clearInterval(healthIntervalRef.current);
      healthIntervalRef.current = null;
    }

    setRecordingState('STOPPING');
    const totalDurationSeconds = Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000));

    try {
      // 1. Await MediaRecorder stop event to ensure all buffered chunks are flushed
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        await new Promise<void>((resolve) => {
          const rec = mediaRecorderRef.current;
          if (!rec || rec.state === 'inactive') {
            resolve();
            return;
          }
          rec.addEventListener('stop', () => resolve(), { once: true });
          try {
            rec.stop();
          } catch {
            resolve();
          }
        });
      }

      // 2. Inspect active video tracks before stopping
      const hadVideoTracks = Boolean(compositeStreamRef.current && compositeStreamRef.current.getVideoTracks().length > 0);

      // Stop internal tracks if created internally
      if (internalCameraStreamRef.current) {
        internalCameraStreamRef.current.getTracks().forEach((track) => track.stop());
        internalCameraStreamRef.current = null;
      }
      if (internalAudioStreamRef.current) {
        internalAudioStreamRef.current.getTracks().forEach((track) => track.stop());
        internalAudioStreamRef.current = null;
      }
      if (compositeStreamRef.current) {
        compositeStreamRef.current.getTracks().forEach((track) => track.stop());
        compositeStreamRef.current = null;
      }

      setRecordingState('UPLOADING');

      // 3. Assemble composite recording blob
      const effectiveMimeType = mediaRecorderRef.current?.mimeType ||
        uploadInitDetailsRef.current?.mimeType ||
        (hadVideoTracks ? 'video/webm' : (captureMode === 'AUDIO_ONLY' ? 'audio/webm' : 'video/webm'));
      const recordingBlob = new Blob(recordedChunksRef.current, { type: effectiveMimeType });
      const fileSize = recordingBlob.size;

      const initData = uploadInitDetailsRef.current;
      let publicUrl: string | undefined = undefined;
      const apiBase = getApiBaseUrl();

      // Immediately cache local blob in IndexedDB & sessionStorage for persistent instant replay
      if (typeof window !== 'undefined' && fileSize > 0 && attemptId) {
        try {
          await saveSessionRecordingBlob(attemptId, recordingBlob);
          const localBlobUrl = URL.createObjectURL(recordingBlob);
          createdBlobUrlsRef.current.push(localBlobUrl);
          sessionStorage.setItem(`trainova_attempt_video_${attemptId}`, localBlobUrl);
          publicUrl = localBlobUrl;
        } catch { }
      }

      // 4. Direct Upload Execution & Multi-Tier Fallback
      if (fileSize > 0) {
        let directUploadSucceeded = false;
        if (initData && initData.uploadUrl) {
          try {
            const controller = new AbortController();
            // Adaptive upload timeout: minimum 60s, scaled up for larger multi-minute recordings (up to 180s)
            const timeoutDurationMs = Math.max(60000, Math.min(180000, Math.ceil(fileSize / 50000) * 1000));
            const uploadTimeout = setTimeout(() => controller.abort(), timeoutDurationMs);

            const uploadEndpoint = initData.uploadUrl.startsWith('http')
              ? initData.uploadUrl
              : `${apiBase}${initData.uploadUrl.startsWith('/') ? '' : '/'}${initData.uploadUrl}`;

            if (initData.storageProvider === 'cloudinary') {
              const formData = new FormData();
              Object.entries(initData.uploadFields || {}).forEach(([key, val]) => {
                formData.append(key, String(val));
              });
              formData.append('file', recordingBlob, `${initData.storageKey || 'recording'}.webm`);

              const uploadRes = await fetch(uploadEndpoint, {
                method: 'POST',
                body: formData,
                signal: controller.signal,
              });
              clearTimeout(uploadTimeout);

              if (uploadRes.ok) {
                const cloudJson = await uploadRes.json();
                publicUrl = cloudJson.secure_url || cloudJson.url;
                directUploadSucceeded = true;
              }
            } else if (initData.uploadMethod === 'PUT') {
              const putRes = await fetch(uploadEndpoint, {
                method: 'PUT',
                headers: { 'Content-Type': effectiveMimeType, ...(initData.uploadHeaders || {}) },
                body: recordingBlob,
                signal: controller.signal,
              });
              clearTimeout(uploadTimeout);
              if (putRes.ok) {
                const isS3 = initData.storageProvider === 's3' || uploadEndpoint.includes('amazonaws.com');
                if (isS3) {
                  publicUrl = undefined;
                } else {
                  const downloadPath = initData.uploadUrl.startsWith('/api')
                    ? initData.uploadUrl.replace('/upload', '/download')
                    : `/api/v1/mock-storage/download?key=${encodeURIComponent(initData.storageKey)}`;
                  publicUrl = initData.publicUrl || `${apiBase}${downloadPath.startsWith('/') ? '' : '/'}${downloadPath}`;
                }
                directUploadSucceeded = true;
              }
            }
          } catch (uploadNetErr: any) {
            console.warn('[SessionMeetingRecorder] Direct storage upload skipped / timed out:', uploadNetErr?.message || uploadNetErr);
          }
        }

        // Tier 2 Fallback: Local persistent mock disk storage
        if (!directUploadSucceeded && (!publicUrl || publicUrl.startsWith('blob:'))) {
          try {
            const fallbackKey = initData?.storageKey || `tenants/default/attempts/${attemptId}/recording/${Date.now()}.webm`;
            const mockUploadRes = await fetch(`${apiBase}/api/v1/mock-storage/upload?key=${encodeURIComponent(fallbackKey)}`, {
              method: 'PUT',
              headers: { 'Content-Type': effectiveMimeType },
              body: recordingBlob,
            });
            if (mockUploadRes.ok) {
              publicUrl = `${apiBase}/api/v1/mock-storage/download?key=${encodeURIComponent(fallbackKey)}`;
              directUploadSucceeded = true;
            }
          } catch (fallbackErr: any) {
            console.warn('[SessionMeetingRecorder] Local fallback upload note:', fallbackErr?.message || fallbackErr);
          }
        }
      }

      // 5. Notify Backend of Upload Completion
      let completedData: any = null;
      if (attemptId) {
        const persistentPublicUrl = (publicUrl && !publicUrl.startsWith('blob:')) ? publicUrl : undefined;
        const hasLiveVideoTracks = hadVideoTracks;
        const effectiveCaptureMode = (captureMode !== 'AUDIO_ONLY' || hasLiveVideoTracks) ? (hasLiveVideoTracks ? 'AUDIO_VIDEO' : captureMode) : 'AUDIO_ONLY';
        const completeRes = await videoApi.completeRecording(attemptId, {
          recordingId: initData?.recordingId,
          storageKey: initData?.storageKey,
          publicUrl: persistentPublicUrl,
          fileSize,
          durationSeconds: totalDurationSeconds,
          metadata: {
            mimeType: effectiveMimeType,
            captureMode: effectiveCaptureMode,
            chunksCount: recordedChunksRef.current.length,
            cameraWasOn: Boolean(effectiveCaptureMode === 'AUDIO_VIDEO'),
          },
        });

        if (completeRes.success) {
          completedData = completeRes.data;
          setRecordingState('UPLOADED');
          if (onRecordingComplete) {
            onRecordingComplete(completeRes.data);
          }
        } else {
          setRecordingState('UPLOADED');
        }
      } else {
        setRecordingState('UPLOADED');
      }

      recordedChunksRef.current = [];
      return completedData || { publicUrl, fileSize, durationSeconds: totalDurationSeconds };
    } catch (stopErr: any) {
      recordedChunksRef.current = [];
      console.error('[SessionMeetingRecorder] stopRecording error:', stopErr);
      setRecordingState('FAILED');
      setError(stopErr.message || 'Failed to complete session recording upload');
      if (attemptId) {
        await videoApi.failRecording(attemptId, stopErr.message).catch(() => { });
      }
      if (onError) onError(stopErr.message);
      return null;
    }
  }, [recordingState, attemptId, captureMode, onRecordingComplete, onError]);

  useEffect(() => {
    if (autoStart && attemptId && recordingState === 'IDLE') {
      startRecording();
    }
  }, [autoStart, attemptId, recordingState, startRecording]);

  // Dynamically sync video track if cameraStream becomes available or is enabled after recorder start
  useEffect(() => {
    if (recordingState === 'RECORDING' && compositeStreamRef.current && cameraStream) {
      const activeVideo = cameraStream.getVideoTracks().find((t) => t.readyState === 'live');
      if (activeVideo) {
        const existingTracks = compositeStreamRef.current.getVideoTracks();
        if (!existingTracks.includes(activeVideo)) {
          // Remove old stopped tracks
          existingTracks.forEach((ot) => {
            if (ot !== activeVideo) {
              compositeStreamRef.current?.removeTrack(ot);
            }
          });
          compositeStreamRef.current.addTrack(activeVideo);
        }
      }
    }
  }, [recordingState, cameraStream]);

  // Dynamically sync audio track if audioStream becomes available or is enabled after recorder start
  useEffect(() => {
    if (recordingState === 'RECORDING' && compositeStreamRef.current && audioStream) {
      const activeAudio = audioStream.getAudioTracks().find((t) => t.readyState === 'live');
      if (activeAudio) {
        const existingTracks = compositeStreamRef.current.getAudioTracks();
        if (!existingTracks.includes(activeAudio)) {
          existingTracks.forEach((ot) => {
            if (ot !== activeAudio) {
              compositeStreamRef.current?.removeTrack(ot);
            }
          });
          compositeStreamRef.current.addTrack(activeAudio);
        }
      }
    }
  }, [recordingState, audioStream]);



  return {
    recordingState,
    elapsedSeconds,
    error,
    health,
    startRecording,
    stopRecording,
  };
}

